import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationCancel, NavigationEnd, Router } from '@angular/router';
import { Actions, createEffect } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { DataPersistence } from '@nrwl/angular';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { AuthenticationStatus, fromAuthentication } from '@quorum/authentication/state';
import { DeepCopyPipe } from '@quorum/common-pipes';
import {
  Address,
  Associate,
  Customer,
  Employee,
  ServiceAppointment as Appointment,
  AppointmentCase,
  QuoteHeader,
  Vehicle,
  WorkOrder,
  Location,
  Note,
  QuoteSalesDetail,
  PartsRequestDetail,
  AlternateTransportation,
} from '@quorum/models/xs-resource';
import {
  DirectMessageTemplateQueryParameters,
  ActionTypeQueryParameters,
  ServiceAppointmentQueryParameters as AppointmentQueryParameters,
  DeclinedReasonQueryParameters,
  ServiceAppointmentDeclinedMpviCodeQueryParameters,
  ServiceGroupCategoryQueryParameters,
  NoteQueryParameters,
} from '@quorum/models/xs-query';
import {
  GetAppointmentCasesFromServer,
  GetQuoteLinesFromServer,
  GetServiceAppointmentDeclinedOperationsFromServer,
  GetServiceGroupsFromServer,
} from '@quorum/sha-entities/fixed-operations';
import { loadMedia, MediaStateService } from '@quorum/sha-media';
import { fromRouter, Go } from '@quorum/sha-router';
import { ShellComponent as DeclinedOpsShellComponent } from '@quorum/svc-declined-ops-ng-ui';
import {
  fromActionTypes,
  fromDeclinedReasons,
  fromDirectMessageTemplates,
  fromEmployees,
  fromServiceGroupCategories,
} from '@quorum/svc-dropdowns/state';
import { AppointmentFreeformVehicleComponent } from '@quorum/svc-service-lane-ng-ui';
import {
  AppointmentComponent,
  ServiceHistoryComponent,
  ShellComponent,
  TodaysAppointmentsComponent,
  VehicleComponent,
} from '@quorum/svc-service-lane-ng-ui/components';
import {
  fromAcceptedServices,
  fromDeclinedServices,
  fromPendingAcceptedServices,
  fromPendingDeclinedServices,
  fromServicesDue,
  fromMaintenanceSchedule,
  fromTemplateDetail,
} from '@quorum/xsr-vehicle-maintenance/state';
import { FreeformAppointmentMatchingComponent } from 'libs/svc-service-lane-ng-ui/src/lib/freeform-appointment-matching/freeform-appointment-matching.component';
import { CookieService } from 'ngx-cookie-service';
import { forkJoin, from, of, race } from 'rxjs';
import { delay, filter, map, mergeAll, switchMap, take, tap } from 'rxjs/operators';
import {
  ClearCustomerAndVehicleState,
  CreateAppointmentCaseFromQuoteLine,
  CreateAppointmentCaseFromQuoteLineSuccess,
  CreateAppointmentCasesFromQuoteLines,
  CreateAppointmentCasesFromQuoteLinesSuccess,
  CreateAppointmentCaseOnServerSuccess,
  CreateVehicleWithAssociateAndAddressFromServer,
  CreateVehicleWithAssociateAndAddressFromServerSuccess,
  GetCustomerAssociateFromServer,
  GetCustomerAssociateFromServerSuccess,
  GetAllNotesFromServer,
  GetAllNotesFromServerSuccess,
  CreateNotesOnServer,
  CreateCustomerNotesOnServerSuccess,
  CreateAppointmentNotesOnServerSuccess,
  CreateVehicleNotesOnServerSuccess,
  UpdateNotesOnServer,
  UpdateCustomerNotesOnServerSuccess,
  UpdateAppointmentNotesOnServerSuccess,
  UpdateVehicleNotesOnServerSuccess,
  GetOpenAppointmentsFromServer,
  GetOpenAppointmentsFromServerSuccess,
  GetOpenAppointmentsFromServerError,
  GetQuoteFromServer,
  GetSelectedAppointmentCasesFromServerSuccess,
  GetSelectedAppointmentFromServer,
  GetSelectedAppointmentFromServerSuccess,
  GetServiceHistoryFromServer,
  GetServiceHistoryFromServerSuccess,
  GetTodaysAppointmentsFromServer,
  GetTodaysAppointmentsFromServerSuccess,
  GetVehicleFromServer,
  GetVehicleFromServerSuccess,
  ServiceLaneActionTypes,
  GetUnacknowledgedAppointmentsFromServerError,
  GetUnacknowledgedAppointmentsFromServerSuccess,
  GetUnacknowledgedAppointmentsFromServer,
  CreatePartDetailForQuoteLine,
  CreatePartDetailForQuoteLineSuccess,
  UpdateAppointmentFiltersInState,
  CreatePartsRequestDetailForQuoteLine,
  CreatePartsRequestDetailForQuoteLineSuccess,
  GetAlternateTransportationsFromServer,
  GetAlternateTransportationsFromServerSuccess,
  GetAlternateTransportationsFromServerError,
  CreateAlternateTransportationOnServer,
  CreateAlternateTransportationOnServerSuccess,
  CreateAlternateTransportationOnServerError,
  UpdateAlternateTransportationOnServer,
  UpdateAlternateTransportationOnServerSuccess,
  UpdateAlternateTransportationOnServerError,
  DeleteAlternateTransportationOnServer,
  DeleteAlternateTransportationOnServerSuccess,
  DeleteAlternateTransportationOnServerError,
} from './service-lane.actions';
import { ServiceLaneFacade } from './service-lane.facade';
import { ServiceLanePartialState } from './service-lane.reducer';
import { ServiceLaneDropdownsFacade } from '@quorum/service-lane-dropdowns/facade';
import { VehicleMediaGalleryComponent } from 'libs/svc-service-lane-ng-ui/src/lib/vehicle-media-gallery/vehicle-media-gallery.component';
import {
  GetMessageTypesFromServer,
  GetAssociateClassesFromServer,
  GetProvinceStatesFromServer,
  GetServiceCodesFromServer,
  GetTechnicianGroupsFromServer,
  GetEmployeesFromServer,
  GetWarrantySubmissionTypesFromServer,
  GetVehicleColoursFromServer,
  GetVehicleSubTypesFromServer,
  GetVehicleMakesFromServer,
  GetLocationsFromServer,
  GetDirectMessageTemplatesFromServer,
  GetTransportOptionsFromServer,
  GetDeclinedReasonsFromServer,
  GetVehicleInspectionWearPatternsFromServer,
} from '@quorum/service-lane-dropdowns';
import { WorkOrderInspectionShellComponent } from 'libs/svc-service-lane-ng-ui/src/lib/work-order-inspection-shell/work-order-inspection-shell.component';

@Injectable()
export class ServiceLaneEffects {
  currentlyLoadedAppointment: number = null;
  //#region routing and authentication

  go = createEffect(
    () =>
      this.dataPersistence.pessimisticUpdate(fromRouter.NAVIGATE, {
        run: (m: Go, state: any) => {
          this.router.navigate(m.payload.path, m.payload.extras);
        },
        onError: (action: Go, err) => {},
      }),
    { dispatch: false }
  );

  goToRoot = createEffect(
    () =>
      this.dataPersistence.pessimisticUpdate(fromRouter.NAVIGATE_TO_ROOT, {
        run: (m: Go, state: any) => {
          if (state.authentication.employee.jobTitleId == 'TCH') {
            this.router.navigate(['tech']);
          } else {
            this.router.navigate(['']);
          }
        },
        onError: (action: Go, err) => {},
      }),
    { dispatch: false }
  );

  userAuthenticated = createEffect(() =>
    this.dataPersistence.fetch(fromAuthentication.USER_AUTHENTICATED, {
      run: (a: fromAuthentication.UserAuthenticated, state: any) => {
        let actions: Action[] = [];
        actions.push(
          new fromAuthentication.UpdateAuthenticationStages({
            id: 1,
            changes: { authenticationStatus: AuthenticationStatus.completed },
          })
        );

        return from(actions).pipe(delay(500));
      },
      onError: (a: fromAuthentication.UserAuthenticated, error) => {
        return new fromAuthentication.UserAuthenticatedError(error);
      },
    })
  );

  authenticationStages = createEffect(() =>
    this.dataPersistence.fetch(fromAuthentication.UPDATE_AUTHENTICATION_STAGES, {
      run: (a: fromAuthentication.UpdateAuthenticationStages, state: any) => {
        let actions: Action[] = [];
        if (a.payload.id == 1 && a.payload.changes.authenticationStatus == AuthenticationStatus.completed) {
          if (
            this.cookieService.get('X-Source') &&
            this.cookieService.get('X-Source').toLowerCase() !=
              state.authentication.authenticatedUser.user.version.toLowerCase()
          ) {
            this.cookieService.set('X-Source', state.authentication.authenticatedUser.user.version);
            actions.push(
              new fromAuthentication.UpdateAuthenticationStages({
                id: 3,
                changes: { authenticationStatus: AuthenticationStatus.inProgress },
              })
            );
          } else {
            actions.push(
              new fromAuthentication.UpdateAuthenticationStages({
                id: 2,
                changes: { authenticationStatus: AuthenticationStatus.completed },
              })
            );
            actions.push(new fromAuthentication.GetEmployeeFromServer(state.authentication.authenticatedUser.user));
          }
        }
        if (a.payload.id == 3 && a.payload.changes.authenticationStatus == AuthenticationStatus.inProgress) {
          setTimeout(() => {
            location.reload();
          }, 500);
        }

        return from(actions).pipe(delay(500));
      },
      onError: (a: fromAuthentication.UpdateAuthenticationStages, error) => {
        return new fromAuthentication.UserAuthenticatedError(error);
      },
    })
  );

  getEmployeeFromServer = createEffect(() =>
    this.dataPersistence.fetch(fromAuthentication.GET_EMPLOYEE_FROM_SERVER, {
      run: (action: fromAuthentication.GetEmployeeFromServer, state: any) => {
        return this.apiService.get<Employee>(`v/1/associates/employees/${action.payload.xselleratorEmployeeId}`).pipe(
          map((employee) => {
            return new fromAuthentication.GetEmployeeFromServerSuccess(employee);
          })
        );
      },
      onError: (a: fromAuthentication.GetEmployeeFromServer, error) => {
        return new fromAuthentication.GetEmployeeFromServerFailure({
          message: 'Error retrieving user profile.',
          error: error.reason,
        });
      },
    })
  );

  getEmployeeFromServerSuccess = createEffect(() =>
    this.dataPersistence.fetch(fromAuthentication.GET_EMPLOYEE_FROM_SERVER_SUCCESS, {
      run: (action: fromAuthentication.GetEmployeeFromServerSuccess, state: any) => {
        const actions: Action[] = [];

        actions.push(
          new fromAuthentication.UpdateAuthenticationStages({
            id: 4,
            changes: { authenticationStatus: AuthenticationStatus.completed },
          })
        );

        return of(actions).pipe(delay(500), mergeAll());
      },
      onError: (a: fromAuthentication.GetEmployeeFromServerSuccess, error) => {
        return new fromAuthentication.GetEmployeeFromServerFailure(error);
      },
    })
  );
  //#endregion

  //#region customer/associate

  getCustomerAssociateFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneActionTypes.GetCustomerAssociateFromServer, {
      run: (action: GetCustomerAssociateFromServer, state: any) => {
        //if (state.serviceLane.customerAssociate == null) {
        return this.apiService
          .get<Address>(`v/1/associates/associates/${action.payload.id}/addresses`, {
            params: action.payload,
          })
          .pipe(
            map((address) => {
              address = address[0];
              return new GetCustomerAssociateFromServerSuccess({
                associate: address.embedded.associate,
                address: address,
              });
            })
          );
      },
      onError: (action: GetCustomerAssociateFromServer, error) => {
        console.error('Error', error);
      },
    })
  );
  //#endregion

  //#region appointments

  getOpenAppointmentsServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneActionTypes.GetOpenAppointmentsFromServer, {
      run: (action: GetOpenAppointmentsFromServer, state: any) => {
        return this.apiService
          .get<Appointment[]>(`v/2/fixed-operations/appointments`, {
            params: action.payload,
          })
          .pipe(
            map((appointments) => {
              return new GetOpenAppointmentsFromServerSuccess(appointments);
            })
          );
      },
      onError: (action: GetOpenAppointmentsFromServer, error) => {
        console.error('Error', error);
        return new GetOpenAppointmentsFromServerError(error);
      },
    })
  );

  getUnacknowledgedAppointmentsServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneActionTypes.GetUnacknowledgedAppointmentsFromServer, {
      run: (action: GetUnacknowledgedAppointmentsFromServer, state: any) => {
        return this.apiService
          .get<Appointment[]>(`v/2/fixed-operations/appointments`, {
            params: action.payload,
          })
          .pipe(
            map((appointments) => {
              return new GetUnacknowledgedAppointmentsFromServerSuccess(appointments);
            })
          );
      },
      onError: (action: GetUnacknowledgedAppointmentsFromServer, error) => {
        console.error('Error', error);
        return new GetUnacknowledgedAppointmentsFromServerError(error);
      },
    })
  );

  getSelectedAppointmentFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneActionTypes.GetSelectedAppointmentFromServer, {
      run: (action: GetSelectedAppointmentFromServer, state: any) => {
        if (state.serviceLane.todaysAppointments.ids.indexOf(action.payload.id) > -1) {
          return new GetSelectedAppointmentFromServerSuccess(state.todaysAppointments.entities[action.payload.id]);
        } else {
          return this.apiService
            .get<Appointment>(`v/2/fixed-operations/appointments`, {
              params: action.payload,
            })
            .pipe(
              map((appointments) => {
                return new GetSelectedAppointmentFromServerSuccess(appointments[0]);
              })
            );
        }
      },
      onError: (action: GetSelectedAppointmentFromServer, error) => {
        console.log('wtf');
        console.error('Error', error);
      },
    })
  );

  getTodaysAppointmentsServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneActionTypes.GetTodaysAppointmentsFromServer, {
      run: (action: GetTodaysAppointmentsFromServer, state: any) => {
        return forkJoin([
          this.apiService.get<Appointment[]>(`v/2/fixed-operations/appointments`, {
            params: action.payload,
          }),
          action.payload.transportOptionId
            ? of([])
            : this.apiService.get<WorkOrder[]>(`v/2/fixed-operations/work-orders`, {
                params: { ...action.payload, statusId: 'A' },
              }),
        ]).pipe(
          switchMap(([appointments, workOrderAppointments]) => {
            const myAppointmentsToggle = action.payload.serviceAdvisorId ? true : false;
            return from([
              new GetTodaysAppointmentsFromServerSuccess({ appointments, workOrderAppointments, myAppointmentsToggle }),
            ]);
          })
        );
      },
      onError: (action: GetTodaysAppointmentsFromServer, error) => {
        console.error('Error', error);
      },
    })
  );

  getServiceHistoryFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneActionTypes.GetServiceHistoryFromServer, {
      run: (action: GetServiceHistoryFromServer, state: any) => {
        return this.apiService
          .get<WorkOrder[]>(`v/1/fixed-operations/work-orders`, {
            params: action.payload,
          })
          .pipe(
            switchMap((workOrders: WorkOrder[]) => {
              return from([new GetServiceHistoryFromServerSuccess(workOrders)]);
            })
          );
      },
      onError: (action: GetServiceHistoryFromServer, error) => {
        console.error('Error', error);
      },
    })
  );

  getQuoteHeaderFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneActionTypes.GetQuoteFromServer, {
      run: (action: GetQuoteFromServer, state: any) => {
        return this.apiService
          .get<QuoteHeader[]>(`v/1/fixed-operations/quote-headers`, {
            params: action.payload,
          })
          .pipe(
            switchMap((quoteHeaders: QuoteHeader[]) => {
              if (quoteHeaders.length > 0) {
                return from([
                  new GetQuoteLinesFromServer({
                    quoteHeaderId: quoteHeaders[0].id.toString(),
                    embed: 'employeeAssociate,serviceGroup',
                  }),
                ]);
              } else {
                return from([]);
              }
            })
          );
      },
      onError: (action: GetQuoteFromServer, error) => {
        console.error('Error', error);
      },
    })
  );

  loadAppointmentComponent = createEffect(() =>
    this.dataPersistence.navigation(AppointmentComponent, {
      run: (a: ActivatedRouteSnapshot, state: any) => {
        return race(
          this.router.events.pipe(
            filter((events) => {
              return events instanceof NavigationEnd && events.urlAfterRedirects === state.router.state.url;
            }),
            map((event) => {
              return { action: 'finished', event };
            })
          ),
          this.router.events.pipe(
            filter((events) => events instanceof NavigationCancel && events.url === state.router.state.url),
            map((event) => {
              return { action: 'cancelled', event };
            })
          )
        ).pipe(
          switchMap((event) => {
            if (event.action === 'cancelled') {
              return from([]);
            } else if (
              this.currentlyLoadedAppointment != state.router.state.root.firstChild.firstChild.params.appointmentId
            ) {
              this.currentlyLoadedAppointment = state.router.state.root.firstChild.firstChild.params.appointmentId;
              let actions: any = [];
              actions.push(
                new GetSelectedAppointmentFromServer(
                  new AppointmentQueryParameters({
                    id: a.params.appointmentId,
                    embed: 'serviceAdvisorAssociate,employee,serviceCode,employeeAssociate,transportOption',
                  })
                )
              );
              if (a.params.vehicleId && a.params.vehicleId !== 'null') {
                actions.push(new GetQuoteFromServer({ vehicleId: a.params.vehicleId }));
                actions.push(
                  new GetVehicleFromServer({ id: a.params.vehicleId, embed: ['associate', 'type', 'colour'] })
                );
                actions.push(
                  new GetServiceHistoryFromServer({
                    vehicleId: a.params.vehicleId,
                    statusId: 'A,2,C,I,1,P,Q,0',
                    embed: 'cases,declinedMpviCode',
                    pageSize: 30,
                    sort: '-id',
                  })
                );
              }
              actions.push(
                new GetServiceAppointmentDeclinedOperationsFromServer(
                  new ServiceAppointmentDeclinedMpviCodeQueryParameters({
                    appointmentId: a.params.appointmentId,
                    embed: 'serviceGroup, declinedReason',
                  })
                )
              );
              actions.push(
                new GetAppointmentCasesFromServer({
                  params: {
                    appointmentId: a.params.appointmentId,
                    embed: 'warrantySubmissionType,serviceGroup',
                  },
                  returnAction: new GetSelectedAppointmentCasesFromServerSuccess({ appointmentCases: null }),
                })
              );
              actions.push(
                new GetServiceGroupsFromServer({
                  expired: '0',
                  embed: 'serviceGroupServices,serviceGroupRate',
                  useFlag: 'A,R',
                  pageSize: 2000,
                })
              );

              actions.push(loadMedia({ vehicleId: a.params.vehicleId }));
              return of(actions).pipe(mergeAll());
            } else {
              return from([]);
            }
          })
        );
      },
      onError: (a: ActivatedRouteSnapshot, e: any) => {
        console.log(e);
        // we can log and error here and return null
        // we can also navigate back
        return null;
      },
    })
  );

  loadFreeformMatchComponent = createEffect(() =>
    this.dataPersistence.navigation(FreeformAppointmentMatchingComponent, {
      run: (a: ActivatedRouteSnapshot, state: any) => {
        return race(
          this.router.events.pipe(
            filter((events) => {
              return events instanceof NavigationEnd && events.urlAfterRedirects === state.router.state.url;
            }),
            map((event) => {
              return { action: 'finished', event };
            })
          ),
          this.router.events.pipe(
            filter((events) => events instanceof NavigationCancel && events.url === state.router.state.url),
            map((event) => {
              return { action: 'cancelled', event };
            })
          )
        ).pipe(
          switchMap((event) => {
            if (event.action === 'cancelled') {
              return from([]);
            } else {
              let actions: any = [];
              actions.push(
                new GetSelectedAppointmentFromServer(
                  new AppointmentQueryParameters({
                    id: a.params.appointmentId,
                    embed: 'serviceAdvisorAssociate,employee,serviceCode,employeeAssociate,transportOption',
                  })
                )
              );

              return of(actions).pipe(mergeAll());
            }
          })
        );
      },
      onError: (a: ActivatedRouteSnapshot, e: any) => {
        console.log(e);
        // we can log and error here and return null
        // we can also navigate back
        return null;
      },
    })
  );

  loadFreeformVehicleAppointmentComponent = createEffect(() =>
    this.dataPersistence.navigation(AppointmentFreeformVehicleComponent, {
      run: (a: ActivatedRouteSnapshot, state: any) => {
        return race(
          this.router.events.pipe(
            filter((events) => {
              return events instanceof NavigationEnd && events.urlAfterRedirects === state.router.state.url;
            }),
            map((event) => {
              return { action: 'finished', event };
            })
          ),
          this.router.events.pipe(
            filter((events) => events instanceof NavigationCancel && events.url === state.router.state.url),
            map((event) => {
              return { action: 'cancelled', event };
            })
          )
        ).pipe(
          switchMap((event) => {
            if (event.action === 'cancelled') {
              return from([]);
            } else {
              let actions: any = [];
              actions.push(
                new GetSelectedAppointmentFromServer(
                  new AppointmentQueryParameters({
                    id: a.params.appointmentId,
                    embed: 'serviceAdvisorAssociate,employee,serviceCode,employeeAssociate,transportOption',
                  })
                )
              );
              //actions.push(new GetQuoteFromServer({ vehicleId: a.params.vehicleId }));
              actions.push(
                new GetAppointmentCasesFromServer({
                  params: {
                    appointmentId: a.params.appointmentId,
                    embed: 'warrantySubmissionType',
                  },
                  returnAction: new GetSelectedAppointmentCasesFromServerSuccess({ appointmentCases: null }),
                })
              );
              // actions.push(new GetVehicleFromServer({ id: a.params.vehicleId, embed: ['associate'] }));
              // actions.push(
              //   new GetServiceHistoryFromServer({
              //     vehicleId: a.params.vehicleId,
              //     statusId: 'A,2,C,I,1,P,Q',
              //     embed: 'cases,declinedMpviCode'
              //   })
              // );
              actions.push(
                new GetServiceGroupsFromServer({
                  expired: '0',
                  embed: 'serviceGroupServices,serviceGroupRate',
                  useFlag: 'A,R',
                })
              );
              return of(actions).pipe(mergeAll());
            }
          })
        );
      },
      onError: (a: ActivatedRouteSnapshot, e: any) => {
        console.log(e);
        // we can log and error here and return null
        // we can also navigate back
        return null;
      },
    })
  );

  loadServiceHistoryComponent = createEffect(() =>
    this.dataPersistence.navigation(ServiceHistoryComponent, {
      run: (a: ActivatedRouteSnapshot, state: any) => {
        return race(
          this.router.events.pipe(
            filter((events) => {
              return events instanceof NavigationEnd && events.urlAfterRedirects === state.router.state.url;
            }),
            map((event) => {
              return { action: 'finished', event };
            })
          ),
          this.router.events.pipe(
            filter((events) => events instanceof NavigationCancel && events.url === state.router.state.url),
            map((event) => {
              return { action: 'cancelled', event };
            })
          )
        ).pipe(
          switchMap((event) => {
            if (event.action === 'cancelled') {
              return from([]);
            } else {
              let actions: any = [];
              actions.push(
                new GetServiceHistoryFromServer({
                  vehicleId: a.params.vehicleId,
                  statusId: 'A,2,C,I,1,P,Q,0',
                  embed: 'cases,declinedMpviCode',
                  pageSize: 30,
                  sort: '-id',
                })
              );

              return of(actions).pipe(mergeAll());
            }
          })
        );
      },
      onError: (a: ActivatedRouteSnapshot, e: any) => {
        console.log(e);
        // we can log and error here and return null
        // we can also navigate back
        return null;
      },
    })
  );

  loadTodaysAppointmentComponent = createEffect(() =>
    this.dataPersistence.navigation(TodaysAppointmentsComponent, {
      run: (a: ActivatedRouteSnapshot, state: any) => {
        return race(
          this.router.events.pipe(
            filter((events) => {
              return events instanceof NavigationEnd && events.urlAfterRedirects === state.router.state.url;
            }),
            map((event) => {
              return { action: 'finished', event };
            })
          ),
          this.router.events.pipe(
            filter((events) => events instanceof NavigationCancel && events.url === state.router.state.url),
            map((event) => {
              return { action: 'cancelled', event };
            })
          )
        ).pipe(
          switchMap((event) => {
            if (event.action === 'cancelled') {
              return from([]);
            } else {
              this.currentlyLoadedAppointment = null;
              let actions: any = [];
              actions.push(new ClearCustomerAndVehicleState({}));
              actions.push(new fromAcceptedServices.ClearAcceptedServiceInState({}));
              actions.push(new fromDeclinedServices.ClearDeclinedServiceInState({}));
              actions.push(new fromPendingAcceptedServices.ClearPendingAcceptedServiceInState({}));
              actions.push(new fromPendingDeclinedServices.ClearPendingDeclinedServiceInState({}));
              actions.push(new fromServicesDue.ClearServicesDueInState({}));
              actions.push(new fromMaintenanceSchedule.ClearMaintenanceSchedulesInState({}));
              actions.push(new fromTemplateDetail.ClearTemplateDetailInState({}));
              return this.authenticationStateService.selectAuthenticatedEmployee().pipe(
                filter((employee) => employee != null),
                take(1),
                switchMap((employee) => {
                  if (!this.serviceLaneFacade.isEmpty(state.router.state.root.queryParams)) {
                    actions.push(new GetTodaysAppointmentsFromServer(state.router.state.root.queryParams));
                    actions.push(new UpdateAppointmentFiltersInState(state.router.state.root.queryParams));
                  } else {
                    let dateFrom = new Date(state.serviceLane.appointmentFilters.appointmentDateFrom);
                    dateFrom.setHours(0, 0, 0);
                    let dateTo = new Date(state.serviceLane.appointmentFilters.appointmentDateTo);
                    dateTo.setHours(23, 59, 59);
                    let apptQueryParams = new AppointmentQueryParameters({
                      appointmentDateFrom: dateFrom.toISOString(),
                      appointmentDateTo: dateTo.toISOString(),
                      statusId: '1,2',
                      rooftopId: employee.rooftopId.toString(),
                      department: 'M',
                      embed: `'serviceAdvisorAssociate,employee,serviceCode,
                    employeeAssociate,transportOption,customerAssociate,vehicle'`,
                    });

                    if (state.serviceLane.appointmentFilters.serviceCode) {
                      apptQueryParams = {
                        ...apptQueryParams,
                        serviceCode: state.serviceLane.appointmentFilters.serviceCode,
                      };
                    }

                    if (state.serviceLane.appointmentFilters.transportOptionId) {
                      apptQueryParams = {
                        ...apptQueryParams,
                        transportOptionId: state.serviceLane.appointmentFilters.transportOptionId,
                      };
                    }
                    actions.push(new GetTodaysAppointmentsFromServer(apptQueryParams));
                  }
                  let unacknowledgedAppointmentQueryParams = new AppointmentQueryParameters({
                    statusId: '1,2',
                    rooftopId: employee.rooftopId.toString(),
                    department: 'M',
                    digitalCheckInStatus: 'CHECKED_IN',
                    embed: `'serviceAdvisorAssociate,employee,serviceCode,
                  employeeAssociate,transportOption,customerAssociate,vehicle'`,
                  });
                  actions.push(new GetUnacknowledgedAppointmentsFromServer(unacknowledgedAppointmentQueryParams));
                  return of(actions).pipe(mergeAll());
                })
              );
            }
          })
        );
      },
      onError: (a: ActivatedRouteSnapshot, e: any) => {
        console.log(e);
        // we can log and error here and return null
        // we can also navigate back
        return null;
      },
    })
  );

  loadShellComponent = createEffect(() =>
    this.dataPersistence.navigation(ShellComponent, {
      run: (a: ActivatedRouteSnapshot, state: any) => {
        const previousUrl = this.router.url;
        return race(
          this.router.events.pipe(
            filter((events) => {
              return events instanceof NavigationEnd && events.urlAfterRedirects === state.router.state.url;
            }),
            map((event) => {
              return { action: 'finished', event };
            })
          ),
          this.router.events.pipe(
            filter((events) => events instanceof NavigationCancel && events.url === state.router.state.url),
            map((event) => {
              return { action: 'cancelled', event };
            })
          )
        ).pipe(
          switchMap((event) => {
            if (event.action === 'cancelled') {
              return from([]);
            } else {
              const actions: any = [];
              if (state.router.navigationId === 1 || previousUrl === '/login' || previousUrl === '/tech') {
                actions.push(new GetMessageTypesFromServer({}));
                actions.push(new GetAssociateClassesFromServer({}));
                actions.push(new GetProvinceStatesFromServer({}));
                actions.push(new GetServiceCodesFromServer({}));
                actions.push(new GetTechnicianGroupsFromServer({}));
                actions.push(new GetEmployeesFromServer({ embed: ['associate'] }));
                actions.push(new GetWarrantySubmissionTypesFromServer({}));
                actions.push(new GetVehicleColoursFromServer({}));
                actions.push(new GetVehicleSubTypesFromServer({}));
                actions.push(new GetVehicleMakesFromServer({}));
                actions.push(new GetLocationsFromServer({ pageSize: 5000 }));
                actions.push(new GetDirectMessageTemplatesFromServer({}));
                actions.push(new GetTransportOptionsFromServer({}));
                actions.push(new GetDeclinedReasonsFromServer({}));
                actions.push(new GetVehicleInspectionWearPatternsFromServer({}));
                actions.push(
                  new GetServiceGroupsFromServer({
                    expired: '0',
                    embed: 'serviceGroupServices,serviceGroupRate',
                    useFlag: 'A,R',
                    pageSize: 2000,
                  })
                );
              }

              return of(actions).pipe(mergeAll());
            }
          })
        );
      },
      onError: (a: ActivatedRouteSnapshot, e: any) => {
        console.log(e);
        // we can log and error here and return null
        // we can also navigate back
        return null;
      },
    })
  );

  createAppointmentCaseOnServerSuccess = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneActionTypes.CreateAppointmentCaseOnServerSuccess, {
      run: (action: CreateAppointmentCaseOnServerSuccess, state: any) => {},
      onError: (action: CreateAppointmentCaseOnServerSuccess, error) => {},
    })
  );

  createAppointmentCaseFromQuoteLine = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneActionTypes.CreateAppointmentCaseFromQuoteLine, {
      run: (action: CreateAppointmentCaseFromQuoteLine, state: any) => {
        return this.apiService
          .post(
            `v/1/fixed-operations/quote-headers/${action.payload.quoteLine.quoteHeaderId}/quote-details/${action.payload.quoteLine.sequence}/workflows/convert-to-appointment`,
            {
              appointmentId: action.payload.appointmentId,
            }
          )
          .pipe(
            switchMap((appointmentCase: AppointmentCase) => {
              return from([
                new CreateAppointmentCaseFromQuoteLineSuccess({
                  appointmentCase: appointmentCase,
                  quoteLine: action.payload.quoteLine,
                }),
              ]);
            })
          );
      },
      onError: (action: CreateAppointmentCaseFromQuoteLine, error) => {},
    })
  );

  createAppointmentCasesFromQuoteLines = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneActionTypes.CreateAppointmentCasesFromQuoteLines, {
      run: (action: CreateAppointmentCasesFromQuoteLines, state: any) => {
        return this.apiService
          .post(
            `v/1/fixed-operations/quote-headers/${action.payload.quoteHeaderId}/quote-details/workflows/convert-to-appointment`,
            {
              sequences: action.payload.quoteLineSequences,
              appointmentId: action.payload.appointmentId,
            }
          )
          .pipe(
            switchMap((appointmentCases: AppointmentCase[]) => {
              return from([
                new CreateAppointmentCasesFromQuoteLinesSuccess({
                  quoteLineSequences: action.payload.quoteLineSequences,
                  appointmentCases: appointmentCases,
                }),
              ]);
            })
          );
      },
      onError: (action: CreateAppointmentCasesFromQuoteLines, error) => {},
    })
  );
  //#endregion

  //#region vehicle

  loadVehicleComponent = createEffect(() =>
    this.dataPersistence.navigation(VehicleComponent, {
      run: (a: ActivatedRouteSnapshot, state: any) => {
        return race(
          this.router.events.pipe(
            filter((events) => {
              return events instanceof NavigationEnd && events.urlAfterRedirects === state.router.state.url;
            }),
            map((event) => {
              return { action: 'finished', event };
            })
          ),
          this.router.events.pipe(
            filter((events) => events instanceof NavigationCancel && events.url === state.router.state.url),
            map((event) => {
              return { action: 'cancelled', event };
            })
          )
        ).pipe(
          switchMap((event) => {
            if (event.action === 'cancelled') {
              return from([]);
            } else {
              this.currentlyLoadedAppointment = null;
              let actions: any = [];
              actions.push(new GetVehicleFromServer({ id: a.params.vehicleId, embed: ['associate'] }));
              actions.push(
                new GetOpenAppointmentsFromServer({
                  vehicleId: a.params.vehicleId,
                  statusId: '1,2',
                  department: 'M',
                  embed: 'serviceAdvisorAssociate,status',
                })
              );

              return of(actions).pipe(mergeAll());
            }
          })
        );
      },
      onError: (a: ActivatedRouteSnapshot, e: any) => {
        console.log(e);
        // we can log and error here and return null
        // we can also navigate back
        return null;
      },
    })
  );

  getVehicleFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneActionTypes.GetVehicleFromServer, {
      run: (action: GetVehicleFromServer, state: any) => {
        // if (state.serviceLane.vehicle == null ||)  {
        return this.apiService
          .get<Vehicle>(`v/1/vehicles/vehicles/${action.payload.id}`, {
            params: action.payload,
          })
          .pipe(
            switchMap((vehicle: Vehicle) => {
              return from([
                new GetVehicleFromServerSuccess(vehicle),
                new GetCustomerAssociateFromServer({ id: vehicle.associateId, embed: ['associate,location'] }),
              ]);
            })
          );
        // } else {
        //   return from([
        //     new GetVehicleFromServerSuccess(state.serviceLane.vehicle),
        //     new GetCustomerAssociateFromServer({
        //       id: state.serviceLane.vehicle.associateId,
        //       embed: ['associate,location']
        //     })
        //   ]);
        // }
      },
      onError: (action: GetVehicleFromServer, error) => {
        console.error('Error', error);
      },
    })
  );

  //#endregion

  //#region notes
  GetAllNotesFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneActionTypes.GetAllNotesFromServer, {
      run: (action: GetAllNotesFromServer, state: any) => {
        let customerNoteParams: NoteQueryParameters = new NoteQueryParameters();
        customerNoteParams.sourceReference1Id = action.payload.associateId;
        customerNoteParams.functionalAreaId = 'CU';

        let appointmentNoteParams: NoteQueryParameters = new NoteQueryParameters();
        appointmentNoteParams.sourceReference1Id = action.payload.appointmentId;
        appointmentNoteParams.sourceReference2Id = 'N';
        appointmentNoteParams.functionalAreaId = 'SA';

        let vehicleNoteParams: NoteQueryParameters = new NoteQueryParameters();
        vehicleNoteParams.sourceReference1Id = action.payload.vehicleId;
        vehicleNoteParams.sourceReference2Id = 'N';
        vehicleNoteParams.functionalAreaId = 'VE';

        return forkJoin(
          this.apiService.get(`v/1/common/legacy-notes`, { params: customerNoteParams }).pipe(
            map((notes) => {
              return notes.length == 1 ? notes[0] : null;
            })
          ),
          this.apiService.get(`v/1/common/legacy-notes`, { params: appointmentNoteParams }).pipe(
            map((notes) => {
              return notes.length == 1 ? notes[0] : null;
            })
          ),
          this.apiService.get(`v/1/common/legacy-notes`, { params: vehicleNoteParams }).pipe(
            map((notes) => {
              return notes.length == 1 ? notes[0] : null;
            })
          )
        ).pipe(
          switchMap(([customerNotes, appointmentNotes, vehicleNotes]) => {
            return from([new GetAllNotesFromServerSuccess({ customerNotes, appointmentNotes, vehicleNotes })]);
          })
        );
      },
      onError: (action: GetAllNotesFromServer, error) => {
        console.error('Error', error);
      },
    })
  );

  createNotesOnServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneActionTypes.CreateNotesOnServer, {
      run: (action: CreateNotesOnServer, state: any) => {
        return this.apiService.post(`v/1/common/legacy-notes`, action.payload.newNote).pipe(
          map((newNote: Note) => {
            if (action.payload.noteType == 'customer') {
              return new CreateCustomerNotesOnServerSuccess(newNote);
            } else if (action.payload.noteType == 'appointment') {
              return new CreateAppointmentNotesOnServerSuccess(newNote);
            } else {
              return new CreateVehicleNotesOnServerSuccess(newNote);
            }
          })
        );
      },
      onError: (action: CreateNotesOnServer, error) => {
        console.error('Error', error);
      },
    })
  );

  updateNotesOnServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneActionTypes.UpdateNotesOnServer, {
      run: (action: UpdateNotesOnServer, state: any) => {
        return this.apiService.put(`v/1/common/legacy-notes/${action.payload.newNote.id}`, action.payload.newNote).pipe(
          map((newNote: Note) => {
            if (action.payload.noteType == 'customer') {
              return new UpdateCustomerNotesOnServerSuccess(newNote);
            } else if (action.payload.noteType == 'appointment') {
              return new UpdateAppointmentNotesOnServerSuccess(newNote);
            } else {
              return new UpdateVehicleNotesOnServerSuccess(newNote);
            }
          })
        );
      },
      onError: (action: UpdateNotesOnServer, error) => {
        console.error('Error', error);
      },
    })
  );
  //#endregion

  createVehicleWithAssociateAndAddressOnServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneActionTypes.CreateVehicleWithAssociateAndAddressFromServer, {
      run: (action: CreateVehicleWithAssociateAndAddressFromServer, state: any) => {
        return this.apiService.post<Associate>(`v/1/associates/associates`, action.payload.associate).pipe(
          switchMap((associate): any => {
            /* if locationId is set to a non-numeric string (means a new location name has been entered manually (e.g. 'Oakland)) - need to create a NEW Location first */
            if (isNaN(Number(action.payload.address.locationId))) {
              let newLocation: Location = {
                ...new Location(),
                countryId: action.payload.address.countryId,
                description: action.payload.address.locationId,
                postalCode: null,
                provStateId: action.payload.address.provStateId,
              };

              return this.apiService.post<Location>('v/1/associates/locations', newLocation).pipe(
                switchMap((newlyCreatedLocation) => {
                  let addressWithNewlyCreatedLocation: Address = new Address({
                    ...action.payload.address,
                    associateId: associate.id,
                    locationId: newlyCreatedLocation.id,
                    cellPhoneNumber: action.payload.address.cellPhoneNumber
                      ? action.payload.address.cellPhoneNumber.replace(/\D+/g, '')
                      : null,
                    homePhoneNumber: action.payload.address.homePhoneNumber
                      ? action.payload.address.homePhoneNumber.replace(/\D+/g, '')
                      : null,
                  });

                  return this.apiService
                    .post<Address>(
                      `v/1/associates/associates/${associate.id}/addresses`,
                      addressWithNewlyCreatedLocation
                    )
                    .pipe(
                      map((addressWithNewlyCreatedLocation) => {
                        /* fetch all the locations to have the newly created location available in the Vehicle Location dropdown list */
                        this.serviceLaneDropdownsFacade.getLocationsFromServer();
                        return associate;
                      })
                    );
                })
              );
            } else {
              /*  else the locationId is set to a numeric string (means this location exists and has been matched with on of the addressDropdowns) */
              let address: Address = new Address({ ...action.payload.address, associateId: associate.id });
              return this.apiService.post<Address>(`v/1/associates/associates/${associate.id}/addresses`, address).pipe(
                map((address) => {
                  return associate;
                })
              );
            }
          }),
          switchMap((associate: Associate) => {
            let customer: Customer = {
              ...new Customer(),
              associateId: associate.id,
              creditFlag: '0',
              acceptCheque: '1',
              poFlag: '0',
              customerTypeId: 6,
              wholesalerFlag: '0',
              useCustomerFlag: '0',
              shopFlag: '1',
              workOrderPrintDiscount: '1',
              serviceNoticeFlag: '1',
              printStatements: '1',
              zeroBalanceStatements: '0',
            };
            return this.apiService.post<Customer>(`v/1/associates/customers`, customer).pipe(
              map((customer) => {
                return associate;
              })
            );
          }),
          switchMap((associate) => {
            let vehicle = new DeepCopyPipe().transform(action.payload.vehicle);
            vehicle.associateId = associate.id;
            return this.apiService
              .post<Vehicle>(`v/1/vehicles/vehicles`, vehicle, {
                params: action.payload.vehicleQueryParams ? action.payload.vehicleQueryParams : null,
              })
              .pipe(
                map((vehicle) => {
                  let toastOptions: any = {
                    message: `Vehicle with an associate successfully created`,
                    actionText: null,
                    options: { duration: 3000 },
                  };

                  this.serviceLaneFacade.addToast(toastOptions);
                  return new CreateVehicleWithAssociateAndAddressFromServerSuccess(vehicle);
                })
              );
          })
        );
      },
      onError: (action: CreateVehicleWithAssociateAndAddressFromServer, error) => {
        console.error('Error', error);
      },
    })
  );

  logoutSuccess = createEffect(() =>
    this.dataPersistence.fetch(fromAuthentication.LOGOUT_SUCCESS, {
      run: (a: fromAuthentication.LogoutSuccess, state: any) => {
        this.serviceLaneFacade.clearState();
        return from([
          new fromAuthentication.AddAuthenticationStages([
            {
              id: 1,
              task: 'Authenticate User',
              authenticationStatus: AuthenticationStatus.pending,
            },
            {
              id: 2,
              task: 'Checking Versions',
              authenticationStatus: AuthenticationStatus.pending,
            },
            {
              id: 3,
              task: 'Pulling Newest Version',
              authenticationStatus: AuthenticationStatus.optional,
            },
            {
              id: 4,
              task: 'Retrieving User Profile',
              authenticationStatus: AuthenticationStatus.pending,
            },
          ]),
        ]);
      },
      onError: (a: fromAuthentication.LogoutSuccess, error) => {
        return new fromAuthentication.LogoutError(error);
      },
    })
  );

  loadDeclinedOpsDropdowns = createEffect(() =>
    this.dataPersistence.navigation(DeclinedOpsShellComponent, {
      run: (a: ActivatedRouteSnapshot, state: any) => {
        return race(
          this.router.events.pipe(
            filter((events) => {
              return events instanceof NavigationEnd && events.urlAfterRedirects === state.router.state.url;
            }),
            map((event) => {
              return { action: 'finished', event };
            })
          ),
          this.router.events.pipe(
            filter((events) => events instanceof NavigationCancel && events.url === state.router.state.url),
            map((event) => {
              return { action: 'cancelled', event };
            })
          )
        ).pipe(
          switchMap((event) => {
            if (event.action === 'cancelled') {
              return from([]);
            } else {
              return this.authenticationStateService.selectAuthenticatedEmployee().pipe(
                filter((employee) => employee != null),
                take(1),
                switchMap((employee) => {
                  let actions: any = [];
                  actions.push(
                    new fromServiceGroupCategories.GetServiceGroupCategoriesFromServer(
                      new ServiceGroupCategoryQueryParameters({
                        rooftopId: [employee.rooftopId.toString()],
                      })
                    )
                  );

                  actions.push(new fromEmployees.GetEmployeesFromServer({ embed: ['associate'] }));

                  actions.push(
                    new fromDeclinedReasons.GetDeclinedReasonsFromServer(
                      new DeclinedReasonQueryParameters({ rooftopId: employee.rooftopId.toString() })
                    )
                  );

                  actions.push(
                    new fromDirectMessageTemplates.GetDirectMessageTemplatesFromServer(
                      new DirectMessageTemplateQueryParameters()
                    )
                  );

                  actions.push(new fromActionTypes.GetActionTypesFromServer(new ActionTypeQueryParameters()));

                  return of(actions).pipe(mergeAll());
                })
              );
            }
          })
        );
      },
      onError: (a: ActivatedRouteSnapshot, e: any) => {
        console.log(e);
        // we can log and error here and return null
        // we can also navigate back
        return null;
      },
    })
  );

  loadTechWorkOrderWindow = createEffect(
    () =>
      this.dataPersistence.navigation(WorkOrderInspectionShellComponent, {
        run: (a: ActivatedRouteSnapshot, state: any) => {
          return race(
            this.router.events.pipe(
              filter((events) => {
                return events instanceof NavigationEnd && events.urlAfterRedirects === state.router.state.url;
              }),
              map((event) => {
                return { action: 'finished', event };
              })
            ),
            this.router.events.pipe(
              filter((events) => events instanceof NavigationCancel && events.url === state.router.state.url),
              map((event) => {
                return { action: 'cancelled', event };
              })
            )
          ).pipe(
            switchMap((event) => {
              if (event.action === 'cancelled') {
                return from([]);
              } else {
                let actions: any = [];
                return this.apiService.get(`/v/1/fixed-operations/work-orders/${a.queryParams.workOrderId}`).pipe(
                  switchMap((workOrder) => {
                    return this.authenticationStateService.selectAuthenticatedUser().pipe(
                      filter((authUser) => authUser != null),
                      take(1),
                      tap((authUser) => {
                        this.mediaStateService.loadMediaForVehicle(workOrder.vehicleId);
                        this.mediaStateService.setMediaModelKey({
                          vehicleId: workOrder.vehicleId,
                          workOrderId: workOrder.id,
                          destinationId: authUser.user.destination,
                          customerFacing: true,
                          key: 'workOrderCase',
                        });
                      })
                    );
                  })
                );
                return of(actions).pipe(mergeAll());
              }
            })
          );
        },
        onError: (a: ActivatedRouteSnapshot, e: any) => {
          console.log(e);
          // we can log and error here and return null
          // we can also navigate back
          return null;
        },
      }),
    { dispatch: false }
  );

  loadVehicleMediaGallery = createEffect(() =>
    this.dataPersistence.navigation(VehicleMediaGalleryComponent, {
      run: (a: ActivatedRouteSnapshot, state: any) => {
        return race(
          this.router.events.pipe(
            filter((events) => {
              return events instanceof NavigationEnd && events.urlAfterRedirects === state.router.state.url;
            }),
            map((event) => {
              return { action: 'finished', event };
            })
          ),
          this.router.events.pipe(
            filter((events) => events instanceof NavigationCancel && events.url === state.router.state.url),
            map((event) => {
              return { action: 'cancelled', event };
            })
          )
        ).pipe(
          switchMap((event) => {
            if (event.action !== 'cancelled') {
              this.currentlyLoadedAppointment = null;
            }
            return from([]);
          })
        );
      },
      onError: (a: ActivatedRouteSnapshot, e: any) => {
        console.log(e);
        // we can log and error here and return null
        // we can also navigate back
        return null;
      },
    })
  );

  createPartDetailForQuoteLine = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneActionTypes.CreatePartDetailForQuoteLine, {
      run: (action: CreatePartDetailForQuoteLine, state: any) => {
        return this.apiService.post(`v/1/fixed-operations/quote-sales-details`, action.payload.partDetails).pipe(
          switchMap((partDetails: QuoteSalesDetail) => {
            return from([
              new CreatePartDetailForQuoteLineSuccess({
                partDetails: partDetails,
                quoteLine: action.payload.quoteLine,
              }),
            ]);
          })
        );
      },
      onError: (action: CreatePartDetailForQuoteLine, error) => {},
    })
  );

  createPartsRequestDetailForQuoteLine = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneActionTypes.CreatePartsRequestDetailForQuoteLine, {
      run: (action: CreatePartsRequestDetailForQuoteLine, state: any) => {
        return this.apiService
          .post(
            `v/1/fixed-operations/parts-requests/${action.payload.partsRequest.id}/details`,
            action.payload.partsRequestDetail
          )
          .pipe(
            switchMap((partsRequestDetail: PartsRequestDetail) => {
              return from([
                new CreatePartsRequestDetailForQuoteLineSuccess({
                  partsRequest: action.payload.partsRequest,
                  partsRequestDetail: partsRequestDetail,
                  quoteLine: action.payload.quoteLine,
                }),
              ]);
            })
          );
      },
      onError: (action: CreatePartsRequestDetailForQuoteLine, error) => {},
    })
  );

  getAlternateTransportationsFromServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneActionTypes.GetAlternateTransportationsFromServer, {
      run: (action: GetAlternateTransportationsFromServer, state: any) => {
        return this.apiService
          .get<AlternateTransportation[]>(
            `v/2/service-appointments/${action.payload.appointmentId}/alternate-transportations`,
            {
              params: action.payload,
            }
          )
          .pipe(
            map((alternateTransportations: AlternateTransportation[]) => {
              return new GetAlternateTransportationsFromServerSuccess(alternateTransportations);
            })
          );
      },
      onError: (action: GetAlternateTransportationsFromServer, error) => {
        console.error('Error', error);
        return new GetAlternateTransportationsFromServerError(error);
      },
    })
  );

  createAlternateTransportationOnServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneActionTypes.CreateAlternateTransportationOnServer, {
      run: (action: CreateAlternateTransportationOnServer, state: any) => {
        console.log(action.payload);
        return this.apiService
          .post<AlternateTransportation>(
            `v/2/service-appointments/${action.payload.appointmentId}/alternate-transportations/workflow`,
            action.payload
          )
          .pipe(
            map((alternateTransportation: AlternateTransportation) => {
              return new CreateAlternateTransportationOnServerSuccess(alternateTransportation);
            })
          );
      },
      onError: (action: CreateAlternateTransportationOnServer, error) => {
        console.error('Error', error);
        return new CreateAlternateTransportationOnServerError(error);
      },
    })
  );

  updateAlternateTransportationOnServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneActionTypes.UpdateAlternateTransportationOnServer, {
      run: (action: UpdateAlternateTransportationOnServer, state: any) => {
        return this.apiService
          .put<AlternateTransportation>(
            `v/2/service-appointments/${action.payload.appointmentId}/alternate-transportations/${action.payload.sequence}/workflow`,
            action.payload
          )
          .pipe(
            map((alternateTransportation: AlternateTransportation) => {
              return new UpdateAlternateTransportationOnServerSuccess(alternateTransportation);
            })
          );
      },
      onError: (action: UpdateAlternateTransportationOnServer, error) => {
        console.error('Error', error);
        return new UpdateAlternateTransportationOnServerError(error);
      },
    })
  );

  deleteAlternateTransportationOnServer = createEffect(() =>
    this.dataPersistence.fetch(ServiceLaneActionTypes.DeleteAlternateTransportationOnServer, {
      run: (action: DeleteAlternateTransportationOnServer, state: any) => {
        return this.apiService
          .delete<AlternateTransportation>(
            `v/2/service-appointments/${action.payload.appointmentId}/alternate-transportations/${action.payload.sequence}`
          )
          .pipe(
            map((results) => {
              return new DeleteAlternateTransportationOnServerSuccess(action.payload);
            })
          );
      },
      onError: (action: DeleteAlternateTransportationOnServer, error) => {
        console.error('Error', error);
        return new DeleteAlternateTransportationOnServerError(error);
      },
    })
  );

  constructor(
    private actions$: Actions,
    private dataPersistence: DataPersistence<ServiceLanePartialState>,
    private apiService: ApiService,
    private router: Router,
    private cookieService: CookieService,
    private authenticationStateService: AuthenticationStateService,
    private serviceLaneFacade: ServiceLaneFacade,
    private mediaStateService: MediaStateService,
    private serviceLaneDropdownsFacade: ServiceLaneDropdownsFacade
  ) {}
}
